/**
 * What committing the Custom recurrence dialog should change on the draft.
 *
 * `handleDone` had three branches and only two of them marked the message as
 * recurring. The odd one out is the branch that RE-DATES: when the start the
 * customer picked does not fit the pattern they chose — Wednesdays selected while
 * the date sits on a Thursday, which is an entirely ordinary thing to do — the
 * dialog moved the start to the nearest valid occurrence, stored the rule, and
 * never dispatched `setIsRecurring`.
 *
 * The result was a message that carries a recurrence rule but is not flagged
 * recurring: the customer configures a repeating schedule, has their start date
 * moved without being told, and receives exactly ONE send. It was visible in the
 * dialog itself — the summary line reverted from "every Monday … for 5 times" to
 * the plain "will be sent on <date>" sentence.
 *
 * Expressed as a plan rather than as three sets of dispatches so the invariant is
 * structural: there is one `setRecurring` in the codebase and every path returns
 * it. A future branch cannot forget to include it, because there is nothing to
 * forget.
 */

/**
 * Why the start had to move — the panel says a different sentence for each.
 *
 * `off-pattern`: the chosen start is still ahead but is not on the pattern
 * (Wednesdays picked while the date sits on a Saturday).
 * `passed`: the chosen start has already gone — whether or not it fits. Under
 * FREQ=DAILY every date fits, so treating "fits" as "fine" left every past daily
 * start untouched; that is the hole the live matrix found on 2026-09-11. When a
 * start is BOTH off-pattern and past, `passed` wins: it is the fact the customer
 * most needs, and the one that would stop the message sending at all.
 */
export type StartMoveReason = 'off-pattern' | 'passed';

export interface RecurrenceCommitPlan {
    /** Always true: committing this dialog means the message repeats. */
    setRecurring: boolean;
    /** The rule to store — re-anchored when the start had to move. */
    rruleset: string;
    /** Present ONLY when the chosen start had to move. */
    dueDate?: string;
    /**
     * Whether the customer's chosen start was moved for them.
     *
     * Carried so the caller can SAY SO. Moving someone's date silently is the
     * behaviour this module exists to make visible; suppressing it here would
     * just hide the same problem one level down.
     */
    startMoved: boolean;
    /** Present exactly when `startMoved` is. */
    moveReason?: StartMoveReason;
}

export interface RecurrenceCommitInput {
    /** The rule as configured, anchored at the start the customer chose. */
    rruleset: string;
    /** Does the chosen start actually fall on the pattern? */
    startIsValid: boolean;
    /**
     * Has the chosen start already passed (beyond the shared one-minute grace)?
     * A passed start must move even when it fits — fitting is not an exemption.
     */
    startHasPassed?: boolean;
    /** The nearest start that fits AND is still ahead, as ISO — null when there is none. */
    closestValidStart?: string | null;
    /** `rruleset` re-anchored onto `closestValidStart`. */
    rrulesetAtClosestStart?: string | null;
}

export function planRecurrenceCommit(input: RecurrenceCommitInput): RecurrenceCommitPlan {
    const { rruleset, startIsValid, closestValidStart, rrulesetAtClosestStart } = input;
    const startHasPassed = input.startHasPassed === true;

    if ((!startIsValid || startHasPassed) && closestValidStart && rrulesetAtClosestStart) {
        return {
            setRecurring: true,
            rruleset: rrulesetAtClosestStart,
            dueDate: closestValidStart,
            startMoved: true,
            moveReason: startHasPassed ? 'passed' : 'off-pattern',
        };
    }

    // Either the chosen start already fits and is still ahead, or nothing that
    // fits is — in both cases the rule is stored as configured and the start is
    // left alone. A start left alone in the past is not waved through: the
    // compose dialog's Send gate refuses it and says why.
    return { setRecurring: true, rruleset, startMoved: false };
}
